import { memo, useState, useEffect, useContext } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { ContentContext } from '../contexts/ContentContext';
import { ButtonLink } from '../ui/components';
import '../styles/navbar.css';

const EXTERNAL_URL = /^(?:https?:|mailto:|tel:)/i;

function NavigationItem({ item, onNavigate }) {
  const isExternal = EXTERNAL_URL.test(item.to);

  if (isExternal) {
    return (
      <a
        href={item.to}
        onClick={onNavigate}
        target={item.newTab ? '_blank' : undefined}
        rel={item.newTab ? 'noopener noreferrer' : undefined}
      >
        {item.label}
      </a>
    );
  }

  return (
    <NavLink
      to={item.to}
      className={({ isActive }) => (isActive ? 'active' : '')}
      end={item.to === '/'}
      onClick={onNavigate}
    >
      {item.label}
    </NavLink>
  );
}

const Navbar = memo(function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { content } = useContext(ContentContext);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile drawer on route change / resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setMobileOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMobile = () => setMobileOpen((o) => !o);
  const closeMobile = () => setMobileOpen(false);

  const fallbackNavLinks = [
    { to: '/', label: 'Home' },
    { to: '/features', label: 'Platform' },
    { to: '/pricing', label: 'Pricing' },
    { to: '/onboarding', label: 'Onboarding' },
    { to: '/contact', label: 'Contact' },
  ];
  const navigation = content.navigation || {};
  const navLinks = (navigation.links?.length ? navigation.links : fallbackNavLinks)
    .map(link => ({
      label: link.label || 'Untitled',
      newTab: Boolean(link.newTab),
      to: link.url || link.href || '/',
    }));
  const testUrl = navigation.testUrl || 'https://app.klaivox.com/preview?uid=dcf9fcb8-c689-4c95-a23f-9d009bca2ea0';
  const ctaUrl = navigation.ctaUrl || '/contact';
  const ctaIsExternal = EXTERNAL_URL.test(ctaUrl);
  const siteName = content.global?.siteName || 'Klaivox';

  return (
    <>
      <nav
        className={`navbar ${scrolled ? 'navbar--scrolled' : 'navbar--transparent'}`}
        aria-label="Main navigation"
      >
        <div className="navbar__inner">
          {/* Logo */}
          <Link to="/" className="navbar__logo" aria-label="Klaivox home">
            <img src="/klaivox_icon_zoomed_2x.svg" alt="" className="navbar__logo-icon" aria-hidden="true" />
            <span className="navbar__logo-text">
              <span className="logo-k">{siteName.slice(0, 1)}</span>{siteName.slice(1)}
            </span>
          </Link>

          {/* Desktop links */}
          <ul className="navbar__links" role="list">
            {navLinks.map(item => (
              <li key={`${item.to}-${item.label}`}>
                <NavigationItem item={item} onNavigate={closeMobile} />
              </li>
            ))}
          </ul>

          {/* Desktop CTA */}
          <div className="navbar__cta">
            <ButtonLink
              to={testUrl}
              className="navbar__cta-btn navbar__cta-btn--outline"
              variant="secondary"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Test the AI demo"
            >
              {navigation.testLabel || 'Test It Now'}
            </ButtonLink>
            <ButtonLink
              to={ctaUrl}
              external={ctaIsExternal}
              className="navbar__cta-btn"
              target={ctaIsExternal ? '_blank' : undefined}
              rel={ctaIsExternal ? 'noopener noreferrer' : undefined}
              aria-label={navigation.ctaLabel || 'Book a demo'}
            >
              {navigation.ctaLabel || 'Book a Demo'}
            </ButtonLink>
          </div>

          {/* Hamburger */}
          <button
            className={`navbar__hamburger ${mobileOpen ? 'open' : ''}`}
            onClick={toggleMobile}
            aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileOpen}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      <div
        className={`navbar__mobile-drawer ${mobileOpen ? 'open' : ''}`}
        aria-hidden={!mobileOpen}
        inert={mobileOpen ? undefined : ''}
      >
        <ul className="navbar__mobile-links" role="list">
          {navLinks.map(item => (
            <li key={`${item.to}-${item.label}`}>
              <NavigationItem item={item} onNavigate={closeMobile} />
            </li>
          ))}
        </ul>
        <ButtonLink
          to={testUrl}
          className="navbar__mobile-cta navbar__mobile-cta--outline"
          variant="secondary"
          target="_blank"
          rel="noopener noreferrer"
        >
          {navigation.testLabel || 'Test It Now'}
        </ButtonLink>
        <ButtonLink
          to={ctaUrl}
          external={ctaIsExternal}
          className="navbar__mobile-cta"
          onClick={closeMobile}
          target={ctaIsExternal ? '_blank' : undefined}
          rel={ctaIsExternal ? 'noopener noreferrer' : undefined}
        >
          {navigation.ctaLabel || 'Book a Demo'}
        </ButtonLink>
      </div>
    </>
  );
});

export default Navbar;
